import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { InvoiceService } from 'src/app/services/invoice.service';
import { InventoryService } from 'src/app/services/inventory.service';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { Pos } from 'src/app/enums/pos.enum';

@Injectable({
  providedIn: 'root'
})
export class WorkshopJobcardQuoteService {

  constructor(
    private invoiceService: InvoiceService,
    private inventoryService: InventoryService,
    private translate: TranslateService,
    private messageService: MessageService
  ) { }

  calculateTotals(jobcard: any, vatInclusive: boolean): void {
    const quote = jobcard?.quote;

    if (quote?.lineItems) {
      let subtotal = 0;
      let tax = 0;
      let discount = 0;
      let discountTax = 0;
      let total = 0;
      let totalWithoutDiscount = 0;

      quote.lineItems.forEach((item: any) => {
        const qty = item.Quantity || 0;
        const unitPrice = item.UnitPrice || 0;
        const taxAmount = item.Tax || 0;
        const unitPriceInclTax = item.UnitPrice_IncludingTax || (unitPrice + taxAmount);
        const itemDiscount = item.TotalDiscount || 0;

        // Add full amount before any discounts
        totalWithoutDiscount += +(unitPriceInclTax * qty).toFixed(2);

        if (vatInclusive) {
          subtotal += +(unitPrice * qty).toFixed(2);
          tax += +(taxAmount * qty).toFixed(2);
          total += +(unitPriceInclTax * qty).toFixed(2);
          discount += +itemDiscount?.toFixed(2) || 0;

          const divisor = (taxAmount !== 0 && unitPrice !== 0) ? (unitPriceInclTax / unitPrice) : 1;
          const calculatedDiscountTax = itemDiscount - (itemDiscount / divisor);
          discountTax += +calculatedDiscountTax.toFixed(2);
        } else {
          subtotal += +(unitPrice * qty).toFixed(2);
          tax += +(taxAmount * qty).toFixed(2);
          total += +(unitPriceInclTax * qty).toFixed(2);
          discount += +itemDiscount?.toFixed(2) || 0;

          const multiplier = (taxAmount !== 0 && unitPrice !== 0) ? ((unitPriceInclTax / unitPrice) - 1) : 0;
          const calculatedDiscountTax = itemDiscount * multiplier;
          discountTax += +calculatedDiscountTax.toFixed(2);
        }
      });

      // Final assignment and rounding
      quote.subtotal = vatInclusive ? subtotal.toFixed(2) : (subtotal - discount).toFixed(2);
      quote.tax = (tax - discountTax) < 0 ? (0).toFixed(2) : (tax - discountTax).toFixed(2);
      quote.discount = discount.toFixed(2);
      quote.totalWithoutDiscount = totalWithoutDiscount.toFixed(2);

      if (vatInclusive) {
        quote.total_line_items = (subtotal + discountTax).toFixed(2);
        quote.total = Math.max(0, total - discount).toFixed(2);
      } else {
        quote.total_line_items = subtotal.toFixed(2);
        quote.total = Math.max(0, total - discount - discountTax).toFixed(2);
      }
    }
  }

  sendQuoteToPOS(
    jobcard: any,
    partnerData: any,
    specialOrderItems: any[],
    manageStock: boolean,
    onShowTakingTooLong: () => void,
    onSendingComplete: () => void,
    callback?: () => void,
    useWorkOrdersForWorkshop: boolean = false
  ): Observable<any> {

    setTimeout(() => {
        onShowTakingTooLong();
    },10000);

    return this.invoiceService.sendInvoiceToPOS(
      jobcard.quote.id,
      specialOrderItems && specialOrderItems.length > 0 ? specialOrderItems : null
    ).pipe(
      tap((invoice) => {
        this.mapOldQuoteToNewFormat(jobcard, invoice);

        if (partnerData.POSSystemID === Pos.SAGE) {
          this.handleSageInvoiceSync(jobcard, invoice, callback);
        } else if (jobcard.quote.externalKey && (invoice.Errors === null || invoice.Errors.length === 0)) {
          this.sentToPosMessage(
            this.translate.instant(
              specialOrderItems && specialOrderItems.length > 0
                ? (useWorkOrdersForWorkshop ? 'bikeshop.workshop.jobcard.special_order_synced' : 'bikeshop.modal.update_job_card.quote.order_message') : useWorkOrdersForWorkshop ? 'bikeshop.workshop.jobcard.work_order_synced'
                  : 'bikeshop.modal.update_job_card.quote.message_pos_updated'
            )
          );
          if (callback) callback();
        } else {
          this.handleInvoiceErrors(invoice, callback);
        }

        if (manageStock && partnerData.ManageStock && partnerData.AddStockBackAtSendToPOS && partnerData.POSSystemID === 6) {
          setTimeout(() => {
            this.inventoryService.addBackStockFromQuote(jobcard.partnerID, jobcard.id).subscribe();
          }, 2000);
        }

        onSendingComplete();
      })
    );
  }

  createSalesOrder(
    invoiceId: number,
    specialOrderItems: any[],
    onShowTakingTooLong: () => void,
    onSendingComplete: () => void,
    callback?: () => void
  ): Observable<any> {
    onShowTakingTooLong();

    return this.invoiceService.sendInvoiceToPOSAsSalesOrder(
      invoiceId,
      specialOrderItems && specialOrderItems.length > 0 ? specialOrderItems : null
    ).pipe(
      tap((invoice) => {
        this.sentToPosMessage(
          this.translate.instant('bikeshop.modal.update_job_card.quote.message_pos_updated')
        );
        onSendingComplete();
      })
    );
  }

  manageInvoicePOSExtras(
    jobcard: any,
    updatePrices: boolean = false,
    onLoadingStart?: () => void,
    onLoadingComplete?: () => void
  ): Observable<any> {
    if (onLoadingStart) onLoadingStart();

    return this.invoiceService.manageInvoicePOSExtras(jobcard.quote.id).pipe(
      tap((invoice) => {
        if(invoice.ServiceStatusID && jobcard.statusID !== invoice.ServiceStatusID){
          jobcard.statusID = invoice.ServiceStatusID;
        }

        this.mapOldQuoteToNewFormat(jobcard, invoice, true, updatePrices);
        if (onLoadingComplete) onLoadingComplete();
      }, err => {
        if (onLoadingComplete) onLoadingComplete();
      })
    );
  }

  mapOldQuoteToNewFormat(
    jobcard: any,
    oldQuote: any,
    getInventory: boolean = false,
    updatePrices: boolean = false,
    saveStateCallback?: () => void
  ): void {
    if (oldQuote && oldQuote.LineItems) {
      jobcard.quote = {
        ...jobcard.quote,
        statusID: oldQuote.StatusID,
        approvalSent: oldQuote.SentApproval,
        orderNeeded: oldQuote.OrderNeeded,
        workOrderID: oldQuote.WorkOrderID,
        externalKey: oldQuote.ExternalKey,
        externalRefNo: oldQuote.ExternalRefNo,
        synchFeedback: oldQuote.SynchFeedback
      };

      //Remove any line items that were deleted in the old quote
      const lineItemsToRemove = jobcard.quote.lineItems.filter((lineItem: any) => {
        return !oldQuote.LineItems.some((oldLineItem: any) =>
          (!lineItem.ID && oldLineItem.InternalProductID === lineItem.InternalProductID) || (lineItem.ID > 0 && oldLineItem.ID === lineItem.ID)
        );
      });
      jobcard.quote.lineItems = jobcard.quote.lineItems.filter((lineItem: any) => !lineItemsToRemove.includes(lineItem));

      jobcard.quote.lineItems.forEach((lineItem: any) => {
        const oldLineItems = oldQuote.LineItems.filter((item: any) =>
          (!lineItem.ID && item.InternalProductID === lineItem.InternalProductID) || (lineItem.ID > 0 && item.ID === lineItem.ID)
        );
        if (oldLineItems && oldLineItems.length > 0) {
          oldLineItems.forEach((oldLineItem: any) => {
            lineItem.ID = oldLineItem.ID;
            lineItem.LineItemNote = oldLineItem.LineItemNote;
            lineItem.ExternalKey = oldLineItem.ExternalKey;
            lineItem.ExternalProductID = oldLineItem.ExternalProductID;
            lineItem.SKU = oldLineItem.SKU;
            lineItem.InternalProductID = oldLineItem.InternalProductID;
            lineItem.HasBeenUpdated = oldLineItem.HasBeenUpdated;
            lineItem.IsLabor = oldLineItem.IsLabor;
            // The POS API responses (Square etc.) don't include the labour flag, so keep the one we have
            if (oldLineItem.MarkedAsLabourProduct !== undefined) {
              lineItem.MarkedAsLabourProduct = oldLineItem.MarkedAsLabourProduct;
            }

            if (lineItem.SpecialOrderStatus != oldLineItem.SpecialOrderStatus) {
              lineItem.SpecialOrderStatus = oldLineItem.SpecialOrderStatus;
            }
            if (getInventory) {
              lineItem.FoundInPOS = oldLineItem.FoundInPOS;
              lineItem.StockOnHand = oldLineItem.StockOnHand;
              lineItem.OutOfStock = oldLineItem.Note && oldLineItem.Note === 'ORDER OFFERED';
              lineItem.SpecialOrderStatus = oldLineItem.SpecialOrderStatus;
            }
            if(lineItem.IsLabor || lineItem.Quantity != oldLineItem.Quantity){
              lineItem.Quantity = oldLineItem.Quantity;  
            }

            if(lineItem.UnitPrice != oldLineItem.UnitPrice){
              lineItem.UnitPrice = oldLineItem.UnitPrice;
              lineItem.UnitPrice_IncludingTax = oldLineItem.UnitPrice_IncludingTax;
              lineItem.Tax = oldLineItem.Tax;
            }

            if(lineItem.Discount != oldLineItem.Discount || lineItem.DiscountID !== oldLineItem.DiscountID){
              lineItem.Discount = oldLineItem.Discount;
              lineItem.DiscountID = oldLineItem.DiscountID;
              lineItem.TotalDiscount = oldLineItem.Discount;              
            }

            if (updatePrices) {
              lineItem.Quantity = oldLineItem.Quantity;
              lineItem.UnitPrice = oldLineItem.UnitPrice;
              lineItem.UnitPrice_IncludingTax = oldLineItem.UnitPrice_IncludingTax;
              lineItem.Tax = oldLineItem.Tax;
              lineItem.Discount = oldLineItem.Discount;
              lineItem.DiscountID = oldLineItem.DiscountID;
              lineItem.TotalDiscount = oldLineItem.Discount;
              if (saveStateCallback) saveStateCallback();
            }
          });
        }
      });

      if (oldQuote.LineItems.length !== jobcard.quote.lineItems.length) {
        //Look for line items that exist in old quote but not in current jobcard quote and add them
        oldQuote.LineItems.forEach((oldLineItem: any) => {
          const existingLineItemIndex = jobcard.quote.lineItems.findIndex((lineItem: any) => lineItem.ID > 0 && oldLineItem.ID === lineItem.ID);
          if (existingLineItemIndex === -1) {
            jobcard.quote.lineItems.push(oldLineItem);
          }
        });
      }

    }
  }

  updateLineItemPricesByGroup(
    jobcard: any,
    priceGroup: string,
    onLoadingStart?: () => void,
    onLoadingComplete?: () => void
  ): Observable<any> {
    if (onLoadingStart) onLoadingStart();

    return this.invoiceService.updateLineItemPricesByGroup(jobcard.quote.id, priceGroup).pipe(
      tap((invoice) => {
        if (onLoadingComplete) onLoadingComplete();
      })
    );
  }

  private handleSageInvoiceSync(jobcard: any, invoice: any, callback?: () => void): void {
    const externalKeysSynced = invoice.LineItems.every((lineItem: any) =>
      lineItem.ExternalKey && lineItem.ExternalKey.length > 0
    );

    if (!externalKeysSynced) {
      this.invoiceService.saveInvoiceZA(invoice, true).subscribe(
        (invoiceZA) => {
          const stillNotSynced = invoiceZA.LineItems.some((lineItem: any) =>
            !lineItem.ExternalKey || lineItem.ExternalKey.length === 0
          );

          if (stillNotSynced) {
            this.messageService.add({
              life: 5000,
              detail: this.translate.instant('bikeshop.modal.update_job_card.quote.message_pos_not_updated') +
                ' ' + invoiceZA.Errors.join(' and ')
            });
          } else {
            this.sentToPosMessage(
              this.translate.instant('bikeshop.modal.update_job_card.quote.message_pos_updated')
            );
            if (callback) callback();
          }
        },
        (error) => {
          this.messageService.add({
            life: 5000,
            detail: this.translate.instant('bikeshop.modal.update_job_card.quote.message_pos_not_updated') +
              ' ' + invoice.Errors.join(' and ')
          });
        }
      );
    } else {
      this.sentToPosMessage(
        this.translate.instant('bikeshop.modal.update_job_card.quote.message_pos_updated')
      );
      if (callback) callback();
    }
  }

  private handleInvoiceErrors(invoice: any, callback?: () => void): void {
    this.translate.get('bikeshop.modal.update_job_card.quote.message_pos_not_updated').subscribe((msg) => {
      const message = msg + ' ' + invoice.Errors.join(' and ');
      this.sentToPosMessage(message);
      if (callback) callback();
    });
  }

  private sentToPosMessage(message: string): void {
    this.messageService.add({
      life: 5000,
      detail: message
    });
  }

  public calculateServiceTypeHours(jobcard: any): void {
    jobcard.serviceDuration = 0;
    if (jobcard && jobcard.selectedServiceTypes && jobcard.selectedServiceTypes.length > 0) {
      jobcard.selectedServiceTypes.forEach((serviceType: any) => {
        jobcard.serviceDuration += serviceType.hours;
      });
    }
  }
  getDurationFormatted(duration: number) {
    if (isNaN(duration) || duration < 0) return `0${this.translate.instant("general.mins")}`;

    const hours = Math.floor(duration);
    const minutes = Math.round((duration - hours) * 60);

    // Handle durations less than 1 hour
    if (hours === 0) {
      return `${minutes}${this.translate.instant("general.mins")}`;
    }

    // Handle exact hours (no remainder)
    if (minutes === 0) {
      return `${hours}${hours === 1 ? this.translate.instant("general.hr") : this.translate.instant("general.hrs")}`;
    }

    // Handle hours + minutes
    return `${hours}${hours === 1 ? this.translate.instant("general.hr") : this.translate.instant("general.hrs")} ${minutes}${this.translate.instant("general.mins")}`;
  }
}